import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';

import { MessageDirection } from 'models/twilio';
import { COLORS, BEHAVIOR, POSITIONING } from 'components/twilio/constants';
import { renderTimeOnly } from 'components/twilio/utils/renderTime';
import generateMessageId from 'components/twilio/utils/generateMessageId';
import { useTranslation } from 'react-i18next';

const outgoingStyles = `
  background: ${COLORS.SKY_BLUE};
  color: ${COLORS.WHITE};
  :last-child {
    border-bottom-right-radius: 0;
  }
`;

const incomingStyles = `
  background: ${COLORS.WHITE};
  color: ${COLORS.TEXT_DARK};
  :last-child {
    border-bottom-left-radius: 0;
  }
`;

const MessageContainer = styled.pre<{ $isOutgoing: boolean }>`
  font-family: Lato, san-serif;
  white-space: pre-wrap;
  border-radius: ${POSITIONING.BORDER_RADIUS};
  padding: 8px;
  margin: 2px 0;
  width: fit-content;
  max-width: ${BEHAVIOR.CHAT_GROUP_MAX_WIDTH};
  height: fit-content;
  word-break: break-word;
  position: relative;
  overflow: hidden;
  ${({ $isOutgoing }) => ($isOutgoing ? outgoingStyles : incomingStyles)}

  .twilio-message-anchor {
    color: ${({ $isOutgoing }) => ($isOutgoing ? COLORS.WHITE : COLORS.SKY_BLUE)};
  }
`;

const MessageTimestamp = styled.div<{ $isOutgoing: boolean }>`
  font-size: 11px;
  margin-top: 2px;
  text-transform: uppercase;
  display: flex;
  justify-content: ${({ $isOutgoing }) => ($isOutgoing ? 'flex-end' : 'flex-start')};
`;

interface RequiredBubbleProps {
  direction: MessageDirection;
  index: number;
  dateCreated: Date;
}

const DefaultBubbleWrapper: FC<RequiredBubbleProps & { displayNode: ReactNode }> = ({
  direction,
  index,
  dateCreated,
  displayNode,
}) => {
  const { t } = useTranslation();

  return (
    <MessageContainer
      id={generateMessageId(index)}
      $isOutgoing={direction === MessageDirection.OUTGOING}
    >
      {displayNode}
      <MessageTimestamp $isOutgoing={direction === MessageDirection.OUTGOING}>{renderTimeOnly(dateCreated, t)}</MessageTimestamp>
    </MessageContainer>
  );
};

export default React.memo(DefaultBubbleWrapper);
